import { useEffect, useRef } from 'react';

const HeroSection = () => {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const elements = [titleRef.current, subtitleRef.current, ctaRef.current];
    elements.forEach((el, i) => {
      if (!el) return;
      el.style.opacity = '0';
      el.style.transform = 'translateY(40px)';
      setTimeout(() => {
        if (!el) return;
        el.style.transition = 'opacity 0.9s ease, transform 0.9s ease';
        el.style.opacity = '1';
        el.style.transform = 'translateY(0)';
      }, 300 + i * 200);
    });
  }, []);

  const handleWhatsApp = () => {
    window.open(
      'https://wa.me/525525261379?text=Hola%2C%20me%20interesa%20cotizar%20pantallas%20para%20mi%20evento',
      '_blank'
    );
  };

  return (
    <section
      id="hero"
      className="relative w-full min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=3x2%20meter%20LED%20display%20screens%20on%20modern%20trade%20show%20booth%20stand%2C%20sleek%20large%203x2m%20LED%20screen%20at%20corporate%20expo%2C%20cyan%20blue%20ambient%20lighting%2C%20dark%20professional%20exhibition%20hall%2C%20large%20format%20LED%20displays%20for%20brand%20presentations%2C%20premium%20event%20technology%20setup%2C%20cinematic%20wide%20angle%20photography&width=1920&height=1080&seq=hero003&orientation=landscape"
          alt="Pantallas LED en expo tecnológica"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/88 via-black/65 to-black/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_60%_40%,_rgba(6,182,212,0.08)_0%,_transparent_65%)]" />
      </div>

      {/* Decorative cyan particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(14)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-cyan-400/25"
            style={{
              width: `${(i % 3) + 2}px`,
              height: `${(i % 3) + 2}px`,
              left: `${(i * 7) % 100}%`,
              top: `${(i * 13) % 100}%`,
              animation: `float ${3 + (i % 4)}s ease-in-out infinite`,
              animationDelay: `${(i % 3) * 0.8}s`,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 py-32 flex flex-col items-start">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 border border-cyan-500/50 bg-cyan-500/10 text-cyan-400 text-xs font-semibold uppercase tracking-widest px-4 py-2 rounded-full mb-8">
          <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse inline-block" />
          Tecnología LED Premium para Expos y Eventos
        </div>

        {/* Title */}
        <h1
          ref={titleRef}
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight max-w-3xl"
        >
          Haz que tu evento{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-sky-500">
            brille
          </span>{' '}
          con pantallas profesionales
        </h1>

        {/* Subtitle */}
        <p
          ref={subtitleRef}
          className="mt-6 text-lg md:text-xl text-gray-300 font-light max-w-xl leading-relaxed"
        >
          Impacto visual de alto nivel para expos, ferias comerciales, conferencias y más. Tecnología LED de última generación con instalación profesional.
        </p>

        {/* CTA Buttons */}
        <div ref={ctaRef} className="mt-10 flex flex-col sm:flex-row gap-4">
          <button
            onClick={handleWhatsApp}
            className="flex items-center justify-center gap-3 bg-gradient-to-r from-cyan-500 to-sky-600 text-white font-bold text-base px-8 py-4 rounded-full hover:scale-105 hover:shadow-[0_0_40px_rgba(6,182,212,0.5)] transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-whatsapp-line text-xl" />
            Cotizar por WhatsApp
          </button>
          <button
            onClick={() => {
              const el = document.querySelector('#services');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="flex items-center justify-center gap-2 border border-white/30 text-white font-medium text-base px-8 py-4 rounded-full hover:border-cyan-400 hover:text-cyan-400 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            Ver Servicios
            <i className="ri-arrow-down-line" />
          </button>
        </div>

        {/* Stats */}
        <div className="mt-16 flex flex-wrap gap-8 md:gap-12">
          {[
            { value: '500+', label: 'Eventos realizados' },
            { value: '8K', label: 'Resolución máxima' },
            { value: '100%', label: 'Clientes satisfechos' },
          ].map((stat) => (
            <div key={stat.label} className="flex flex-col">
              <span className="text-3xl font-extrabold text-cyan-400">{stat.value}</span>
              <span className="text-sm text-gray-400 mt-1">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-gray-500 text-xs tracking-widest uppercase">Scroll</span>
        <i className="ri-arrow-down-s-line text-cyan-500 text-xl" />
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
